import { get, send } from "./api.js";

export type StudentGoalStatus = "active" | "achieved" | "paused" | "abandoned";

export interface StudentGoal {
  id: string;
  title: string;
  description: string | null;
  status: StudentGoalStatus;
  targetDate: string | null;
  repertoireId: string | null;
  repertoireTitle: string | null;
  repertoireComposer: string | null;
  achievedAt: string | null;
  completionNote: string | null;
  familyVisible: boolean;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  linkedPracticeTasks: number;
}

export interface FamilyStudentGoal {
  id: string;
  title: string;
  description: string | null;
  status: "active" | "achieved";
  targetDate: string | null;
  repertoireId: string | null;
  repertoireTitle: string | null;
  repertoireComposer: string | null;
  achievedAt: string | null;
  createdByName?: string | null;
  createdAt?: string;
}

export interface StaffStudentGoals {
  current: StudentGoal[];
  history: StudentGoal[];
  historyPage: number;
  historyPages: number;
  historyTotal: number;
}

export interface FamilyStudentGoals {
  active: FamilyStudentGoal[];
  recentlyAchieved: FamilyStudentGoal[];
}

function staffPath(studentId: string): string {
  return `/admin/students/${encodeURIComponent(studentId)}/goals`;
}

function familyPath(studentId: string): string {
  return `/me/students/${encodeURIComponent(studentId)}/goals`;
}

export async function getStaffStudentGoals(
  studentId: string,
  historyPage = 1,
  historyPageSize = 15,
): Promise<StaffStudentGoals> {
  return get<StaffStudentGoals>(staffPath(studentId), {
    historyPage: String(historyPage),
    historyPageSize: String(historyPageSize),
  });
}

export async function createStudentGoal(
  studentId: string,
  input: {
    title: string;
    description?: string | null;
    targetDate?: string | null;
    repertoireId?: string | null;
    familyVisible: boolean;
  },
): Promise<StudentGoal> {
  const response = await send<{ goal: StudentGoal }>("POST", staffPath(studentId), input);
  return response.goal;
}

export async function updateStudentGoal(
  studentId: string,
  goalId: string,
  input: Partial<{
    title: string;
    description: string | null;
    targetDate: string | null;
    repertoireId: string | null;
    familyVisible: boolean;
    status: StudentGoalStatus;
    completionNote: string | null;
  }>,
): Promise<StudentGoal> {
  const response = await send<{ goal: StudentGoal }>(
    "PATCH",
    `${staffPath(studentId)}/${encodeURIComponent(goalId)}`,
    input,
  );
  return response.goal;
}

export async function addGoalPracticeTask(
  studentId: string,
  goalId: string,
  input: { title: string; description?: string | null },
): Promise<void> {
  await send(
    "POST",
    `${staffPath(studentId)}/${encodeURIComponent(goalId)}/practice-todos`,
    input,
  );
}

export async function getFamilyStudentGoals(studentId: string): Promise<FamilyStudentGoals> {
  return get<FamilyStudentGoals>(familyPath(studentId));
}
